'use client';

import { c as _c } from "react/compiler-runtime";
import { Alert, Stack, Typography } from '@mui/material';
import { RdsCdcCheckScope, SqlSetupCheckStatus } from '@repo/api-gw-sdk';
import { useEffect } from 'react';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { download } from '@/components/buttons/downloadButton';
import { MainDialog } from '@/components/dialogs/mainDialog';
import { CopyableCode } from '@/components/shared/copyableCode';
import { TemporalWorkflowLink } from '@/components/shared/temporalWorkflowLink';
import { useDAL } from '@/contexts/useDAL';
import { useIsDevModeEnabled } from '@/contexts/useIsDevModeEnabled';
import { useTemporalWorkflowUrl } from '@/utils/temporalLink';
import { CheckingIndicator, stepFooterButtons, type StepDialogFadeProps } from './stepCheckControls';
import { SQL_STEP_DESCRIPTION } from '../messages';
import { useCdcCheckRun } from '../useCdcCheckRun';
import { useRdsCdcStatus } from '../useRdsCdcStatus';

// setupScript is rendered from cdc_setup.sh — a shell wrapper around the cluster and per-database SQL,
// so the name has to keep the .sh extension for the downloaded file to be runnable.
const SETUP_SCRIPT_FILENAME = 'cdc_setup.sh';

// The stock Alert keeps the icon 2px below the first line of text: its 22px icon box and the 16px
// message line each carry their own vertical padding. Sound for a one-line message only, so
// MissingObjects keeps the default and holds its icon at the top of the list.
const ONE_LINE_ALERT_SX = {
  alignItems: 'center',
  '& .MuiAlert-icon': {
    padding: '0'
  }
};
const MissingObjects = t0 => {
  const $ = _c(6);
  const {
    missingObjects
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Typography variant="body2">These CDC objects are missing from the database. Re-run the script above, then check again.</Typography>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      marginTop: "8px",
      paddingLeft: "24px"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== missingObjects) {
    t3 = missingObjects.map(_temp);
    $[2] = missingObjects;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <Alert severity="error">{t1}<Stack component="ul" sx={t2}>{t3}</Stack></Alert>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
export const SqlCommandsDialog = ({
  resourceId,
  onBack,
  onClose,
  ...fadeProps
}: {
  resourceId: string;
  onBack: () => void;
  onClose: () => void;
} & StepDialogFadeProps) => {
  const dal = useDAL();
  const isDevMode = useIsDevModeEnabled();
  const temporalWorkflowUrl = useTemporalWorkflowUrl();
  const {
    data,
    isLoading
  } = dal.inventory.privateLink.useOnboardRdsCdc(resourceId);
  const {
    refresh: refreshPrerequisites,
    isPrivateLinkActive
  } = useRdsCdcStatus(resourceId);
  const check = useCdcCheckRun(resourceId, RdsCdcCheckScope.RDS_CDC_CHECK_SCOPE_FULL);
  const setupScript = data?.sqlInstructions?.setupScript ?? '';
  const isSqlStepDone = check.terminalStatus === SqlSetupCheckStatus.SQL_SETUP_CHECK_STATUS_OK;
  const isSqlStepFailed = check.terminalStatus === SqlSetupCheckStatus.SQL_SETUP_CHECK_STATUS_FAILED;
  const missingObjects = check.verdict?.missingObjects ?? [];

  // The hub and the Backup details row read the heavy prerequisites key. One refresh when the verdict
  // lands keeps them current without tight-polling seconds of AWS checks.
  useEffect(() => {
    if (check.hasVerdictForThisRun) {
      void refreshPrerequisites();
    }
  }, [check.hasVerdictForThisRun, refreshPrerequisites]);
  return <MainDialog isLoading={isLoading} scrollableBody {...fadeProps} header={{
    title: 'Run SQL commands on the database',
    subtitle: SQL_STEP_DESCRIPTION,
    onBack,
    onClose
  }} footer={{
    ...stepFooterButtons({
      isDone: isSqlStepDone,
      // isLoading gates the check buttons: the footer's shape follows the loaded verdict, so a
      // click before the first fetch settles would act on a state the dialog cannot show yet.
      isBusy: check.isVerifying || check.isLoading,
      // The script itself stays readable and downloadable without PrivateLink; only verifying it
      // needs the connection, so the blocker sits on the button rather than on the whole step.
      blockedReason: isPrivateLinkActive ? undefined : 'Available once AWS PrivateLink is active',
      testIdPrefix: 'rds-cdc-sql',
      onContinue: onBack,
      onCheckAndContinue: () => void check.start(),
      onVerifyAgain: () => void check.start()
    })
  }}>
      <Stack sx={{
      gap: '16px',
      paddingX: '40px'
    }}>
        {!!setupScript && <>
            <Alert severity='info' sx={ONE_LINE_ALERT_SX}>
              This script must run with superuser privileges.
            </Alert>
            <CopyableCode code={setupScript} title={SETUP_SCRIPT_FILENAME} tooltipTitle='Creates the eon_replicator role, the eon_publication publication, and the event triggers Eon uses to capture DDL changes.' codeClassName='overflow-y-auto max-h-[40vh]' />
            <ButtonWithIcon variant='outlined' icon='material-symbols-download-rounded' iconPosition='right' text='Download Setup Script' onClick={() => download(setupScript, SETUP_SCRIPT_FILENAME, {
          type: 'text/x-shellscript'
        })} sx={{
          alignSelf: 'flex-start'
        }} />
          </>}

        {check.isVerifying && <CheckingIndicator text='Verifying the setup on your database. This might take a few minutes.' />}

        {!check.isVerifying && isSqlStepDone && <Alert severity='success' sx={ONE_LINE_ALERT_SX}>
            All required CDC objects are present on the database.
          </Alert>}

        {!check.isVerifying && isSqlStepFailed && <Alert severity='error'>
            {check.verdict?.error || 'Verification failed. Try again, or contact support if it keeps failing.'}
          </Alert>}

        {!check.isVerifying && missingObjects.length > 0 && <MissingObjects missingObjects={missingObjects} />}

        {/* The workflow is only identified by a run started from this dialog, so before one the link
            stays visible but disabled rather than disappearing. */}
        {isDevMode && <TemporalWorkflowLink unavailableReason='Developer mode: Run verification first to see Workflow execution' href={check.workflow && temporalWorkflowUrl(check.workflow.namespace, check.workflow.id)} />}
      </Stack>
    </MainDialog>;
};
function _temp(missingObject) {
  return <Typography component="li" variant="body2" key={missingObject}><code>{missingObject}</code></Typography>;
}